/**
 * Lens dispensing jobs: glasses and contact lenses supplied from an issued prescription, from quote
 * through the lab or supplier order to collection. Mirrors /api/lens-dispensing on the REST API, plus
 * the upload of a job's order PDF to the patient's files.
 *
 * Every call acts in Session.practice, which must have the lensDispensingFeature.
 */
app.service("LensDispensingAPI", [
	"$http",
	"ENV",
	"Session",
	function ($http, ENV, Session) {
		const baseUri = ENV.API + "/api/lens-dispensing";

		const getHeaders = function () {
			return {
				Authorization: "Bearer " + Session.token,
				"X-PRACTICE-ID": `${Session.practice.id}`,
			};
		};

		var service = {
			/** Every job in the practice, newest first, each naming its patient. */
			getPracticeJobs: function () {
				return $http({
					method: "GET",
					url: `${baseUri}/jobs`,
					headers: getHeaders(),
				});
			},

			/** A patient's jobs, newest first. */
			getPatientJobs: function (patientId) {
				return $http({
					method: "GET",
					url: `${baseUri}/patients/${encodeURIComponent(patientId)}/jobs`,
					headers: getHeaders(),
				});
			},

			/** One job. A 404 when the practice has no such job. */
			getJob: function (jobId) {
				return $http({
					method: "GET",
					url: `${baseUri}/jobs/${encodeURIComponent(jobId)}`,
					headers: getHeaders(),
				});
			},

			/**
			 * Creates a job, as a quote, from an issued prescription that is in date.
			 * @param {Object} job - { prescription_id, spectacle_rx (PD and heights only), frame, lens,
			 *   extras, trace, supply, fitting_fee, billing, order: { notes, reorder_of_job_id } }
			 */
			createJob: function (job) {
				return $http({
					method: "POST",
					url: `${baseUri}/jobs`,
					headers: getHeaders(),
					data: job,
				});
			},

			/** Replaces what dispensing decides on a job. Refused once ordered. */
			updateJob: function (jobId, job) {
				return $http({
					method: "PUT",
					url: `${baseUri}/jobs/${encodeURIComponent(jobId)}`,
					headers: getHeaders(),
					data: job,
				});
			},

			/**
			 * Invoices a quote.
			 * @param {Object} invoice - { invoice_number, total, amount_due_now, sent_to }
			 */
			invoiceJob: function (jobId, invoice) {
				return $http({
					method: "POST",
					url: `${baseUri}/jobs/${encodeURIComponent(jobId)}/invoice`,
					headers: getHeaders(),
					data: invoice,
				});
			},

			// A payment against an invoiced job: { amount, method, date_paid, account_id } - recorded against
			// its Xero invoice too, where the practice bills there.
			recordPayment: function (jobId, payment) {
				return $http({
					method: "POST",
					url: `${baseUri}/jobs/${encodeURIComponent(jobId)}/payments`,
					headers: getHeaders(),
					data: payment,
				});
			},

			/**
			 * Moves a job on to its next status.
			 * @param {Object} change - { status, lab_name, reference, date_due, document_id } - the
			 *   order details only on ORDERED.
			 */
			advanceJob: function (jobId, change) {
				return $http({
					method: "POST",
					url: `${baseUri}/jobs/${encodeURIComponent(jobId)}/status`,
					headers: getHeaders(),
					data: change,
				});
			},

			/** Appends a note to a job's log. */
			addNote: function (jobId, text) {
				return $http({
					method: "POST",
					url: `${baseUri}/jobs/${encodeURIComponent(jobId)}/notes`,
					headers: getHeaders(),
					data: { text: text },
				});
			},

			/** Deletes a quote. Refused once invoiced. */
			deleteJob: function (jobId) {
				return $http({
					method: "DELETE",
					url: `${baseUri}/jobs/${encodeURIComponent(jobId)}`,
					headers: getHeaders(),
				});
			},
		};

		return service;
	},
]);
